"use client";

import { useLayoutEffect } from "react";

import "@calcom/embed-core/src/embed-iframe";
import { HttpError } from "@calcom/lib/http-error";

type Props = {
  statusCode?: number | null;
  error?: Error | HttpError | null;
  message?: string;
  /** Display debugging information */
  displayDebug?: boolean;
  children?: never;
  reset?: () => void;
};

const defaultProps = {
  displayDebug: false,
};

const ErrorDebugPanel: React.FC<{ error: Props["error"]; children?: never }> = (props) => {
  const { error: e } = props;

  const debugMap = [
    ["error.message", e?.message],
    ["error.name", e?.name],
    ["error.class", e instanceof Error ? e.constructor.name : undefined],
    ["http.url", e instanceof HttpError ? e.url : undefined],
    ["http.status", e instanceof HttpError ? e.statusCode : undefined],
    ["http.cause", e instanceof HttpError ? e.cause?.message : undefined],
    ["error.stack", e instanceof Error ? e.stack : undefined],
  ];

  return (
    <div className="bg-default overflow-hidden shadow sm:rounded-lg">
      <div className="border-subtle border-t px-4 py-5 sm:p-0">
        <dl className="sm:divide-subtle sm:divide-y">
          {debugMap.map(([key, value]) => {
            if (value !== undefined) {
              return (
                <div key={key} className="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 sm:py-5">
                  <dt className="text-emphasis text-sm font-bold">{key}</dt>
                  <dd className="text-emphasis mt-1 text-sm sm:col-span-2 sm:mt-0">{value}</dd>
                </div>
              );
            }
          })}
        </dl>
      </div>
    </div>
  );
};

export const ErrorPage: React.FC<Props> = (props) => {
  const { message, statusCode, error, displayDebug } = { ...defaultProps, ...props };
  const handleReset = () => {
    window.location.reload();
    props.reset?.();
  };

  // useLayoutEffect runs synchronously before browser paint, ensuring it's set early
  useLayoutEffect(() => {
    if (statusCode && typeof window !== "undefined") {
      window.CalComPageStatus = statusCode.toString();
    }
  }, [statusCode]);

  const isNotFound = statusCode === 404;

  return (
    <>
      <div className="flex min-h-screen items-center justify-center bg-[#f4f6f5] p-4">
        <div className="w-full max-w-md rounded-2xl border border-[#e5e7eb] bg-white p-8 shadow-[0_20px_60px_-30px_rgba(7,95,120,0.35)]">
          <p className="text-xs font-semibold uppercase tracking-wide text-[#075F78]">Trafega Pro</p>
          <h1 className="mt-2 bg-gradient-to-r from-brand-deep to-brand bg-clip-text text-5xl font-bold text-transparent">
            {statusCode || "Erro"}
          </h1>
          <h2 className="mt-4 text-xl font-semibold text-gray-900">
            {isNotFound ? "Página não encontrada" : "Algo deu errado por aqui"}
          </h2>
          <p className="mt-2 text-sm text-gray-500">
            {isNotFound
              ? "O endereço que você tentou acessar não existe ou foi movido."
              : "Tivemos um problema inesperado. Você pode tentar de novo ou voltar para o início. Se continuar, avise o suporte."}
          </p>

          {!isNotFound && message ? (
            <pre className="mt-4 w-full whitespace-normal break-words rounded-md bg-[#f4f6f5] p-3 text-xs text-gray-600">
              {message}
            </pre>
          ) : null}

          <div className="mt-6 flex flex-col gap-2 sm:flex-row">
            <button
              type="button"
              onClick={handleReset}
              className="flex-1 rounded-md bg-gradient-to-r from-brand-deep to-brand px-4 py-2.5 text-sm font-medium text-white transition-opacity hover:opacity-90">
              Tentar novamente
            </button>
            <a
              href="/"
              className="flex-1 rounded-md border border-[#075F78] px-4 py-2.5 text-center text-sm font-medium text-[#075F78] transition-colors hover:bg-[#075F78] hover:text-white">
              Voltar ao início
            </a>
          </div>
        </div>
      </div>
      {displayDebug && (
        <div className="flex-wrap">
          <ErrorDebugPanel error={error} />
        </div>
      )}
    </>
  );
};
